import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

export type BookingMenuChoice = 'lesson' | 'service' | 'schedule' | 'group_lesson' | 'inquiry';

interface BookingMenuOption {
  key: BookingMenuChoice | 'coaching';
  label: string;
  title: string;
  /** Keyboard shortcut (lowercase). */
  shortcut: string;
}

/**
 * Booking menu shown when a calendar slot is clicked (Day View, mobile Day
 * View, Week View): a flat list of options, each with a one-key shortcut.
 * "Coaching" expands the studio's coaching packages in place (number keys
 * pick one).
 *
 * The parent owns positioning/dismissal (#custom-popover) and opening the
 * forms; this component only reports which option was picked.
 */
@Component({
  selector: 'app-booking-menu',
  templateUrl: './booking-menu.component.html',
  styleUrls: ['./booking-menu.component.css']
})
export class BookingMenuComponent implements OnChanges {
  @Input() canBookLessons = false;
  @Input() canAddInquiry = false;
  @Input() coachingPackages: { id: string, name: string }[] = [];
  @Input() coachingPackagesLoading = false;
  /** Bumped by the parent each time the popover is shown. */
  @Input() openKey = 0;

  @Output() choose = new EventEmitter<BookingMenuChoice>();
  @Output() coachingChoose = new EventEmitter<{ id: string, name: string }>();
  @Output() close = new EventEmitter<void>();

  showCoaching = false;
  options: BookingMenuOption[] = [];

  private readonly allOptions: BookingMenuOption[] = [
    { key: 'lesson', label: 'Lesson', title: 'Book Lesson', shortcut: 'l' },
    { key: 'service', label: 'Service', title: 'Book Service', shortcut: 's' },
    { key: 'schedule', label: 'Schedule', title: 'Book Schedule', shortcut: 'h' },
    { key: 'group_lesson', label: 'Group Lesson', title: 'Book Group Lesson', shortcut: 'g' },
    { key: 'coaching', label: 'Coaching', title: 'Book Coaching', shortcut: 'c' },
    { key: 'inquiry', label: 'Add Inquiry', title: 'Add Inquiry', shortcut: 'i' },
  ];

  constructor(
    private el: ElementRef<HTMLElement>,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['canBookLessons'] || changes['canAddInquiry']) {
      this.options = this.allOptions.filter(option => option.key === 'inquiry' ? this.canAddInquiry : this.canBookLessons);
    }
    if (changes['openKey']) {
      this.showCoaching = false;
    }
  }

  pick(option: BookingMenuOption): void {
    if (option.key === 'coaching') {
      this.showCoaching = !this.showCoaching;
      return;
    }
    this.choose.emit(option.key);
  }

  trackOption(index: number, option: BookingMenuOption): string {
    return option.key;
  }

  /** Shortcuts work only while the menu is on screen and nothing else has focus. */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.isVisible() || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target?.closest?.('input, textarea, select, [contenteditable="true"], mat-dialog-container')) {
      return;
    }
    const key = event.key.toLowerCase();
    if (key === 'escape') {
      event.preventDefault();
      if (this.showCoaching) {
        this.showCoaching = false;
      } else {
        this.close.emit();
      }
      return;
    }
    if (this.showCoaching) {
      const index = Number(key) - 1;
      if (index >= 0 && index < Math.min(this.coachingPackages.length, 9)) {
        event.preventDefault();
        this.coachingChoose.emit(this.coachingPackages[index]);
      }
      return;
    }
    const option = this.options.find(o => o.shortcut === key);
    if (option) {
      event.preventDefault();
      this.pick(option);
    }
  }

  private isVisible(): boolean {
    return this.el.nativeElement.offsetParent !== null;
  }
}
